/**
 * Public lite self-assessment — lead-gen tool.
 *   Route: /resources/:slug-self-assessment   (slug = "itsm360" today)
 *
 * Orchestrates four phases (intro · questions · result · submitted). State +
 * handlers live in useLiteAssessment; the question and result phases are their
 * own components.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Layout from "../components/Layout";
import useLiteAssessment from "./liteAssessment/useLiteAssessment";
import { trackEvent, EVENTS } from "../lib/analytics";
import LiteQuestions from "./liteAssessment/LiteQuestions";
import LiteResult from "./liteAssessment/LiteResult";

export default function LiteAssessment() {
  const {
    realSlug, lite, answers, setAnswers, result, phase, setPhase,
    submitting, lead, setLead, resumedFromDraft, saveState, completeness,
    computeAndShow, submitLead, radarSeries,
  } = useLiteAssessment();

  if (!lite) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <p className="text-zinc-500">Loading…</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="relative bg-[#0B0B0C] text-white py-20 md:py-28 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 md:px-8 relative z-10">
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <span className="inline-flex items-center px-3 py-1 text-[10.5px] font-bold tracking-[0.18em] text-white bg-[#7737F6] uppercase" data-testid="lite-subbrand-badge">
              {lite.sub_brand}
            </span>
            <span className="text-[11px] font-semibold tracking-[0.22em] text-zinc-400 uppercase">
              EKSELENS360 · Self-assessment
            </span>
          </div>
          <h1 className="ek-heading text-4xl sm:text-5xl lg:text-6xl mb-6" data-testid="lite-title">{lite.title}</h1>
          <p className="text-[#A179FA] text-base md:text-lg font-light italic mb-6" data-testid="lite-subtitle">{lite.subtitle}</p>
          <p className="text-zinc-300 text-base md:text-lg max-w-3xl font-light" data-testid="lite-intro">{lite.intro}</p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">

          {/* Phase: intro */}
          {phase === "intro" && (
            <div data-testid="lite-intro-cta" className="text-center">
              <h2 className="ek-heading text-2xl md:text-3xl mb-4">Ready to start?</h2>
              <p className="text-zinc-600 max-w-2xl mx-auto mb-8 text-[15px]">
                You'll answer {lite.questions.length} questions on a four-point scale: <strong>Reactive · Managed · Proactive · Strategic</strong>. We'll show you your maturity radar and the top three improvement moves we'd talk to you about first.
              </p>
              <button type="button" onClick={() => { trackEvent(EVENTS.ASSESSMENT_START, { type: "lite" }); setPhase("questions"); }} className="ek-btn ek-btn-primary text-[14px]" data-testid="lite-start-btn">
                Start the self-assessment <ArrowRight size={14} />
              </button>
              <p className="text-zinc-500 mt-6 text-[12.5px]">No login. No email required to see the radar. ~3 minutes.</p>
            </div>
          )}

          {/* Phase: questions */}
          {phase === "questions" && (
            <LiteQuestions
              lite={lite}
              answers={answers}
              setAnswers={setAnswers}
              completeness={completeness}
              saveState={saveState}
              resumedFromDraft={resumedFromDraft}
              onShowResult={computeAndShow}
            />
          )}

          {/* Phase: result */}
          {phase === "result" && result && (
            <LiteResult
              lite={lite}
              result={result}
              answers={answers}
              radarSeries={radarSeries}
              lead={lead}
              setLead={setLead}
              submitting={submitting}
              onSubmitLead={submitLead}
              realSlug={realSlug}
            />
          )}

          {/* Phase: submitted */}
          {phase === "submitted" && (
            <div className="text-center max-w-2xl mx-auto" data-testid="lite-submitted">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#7737F6] text-white rounded-full mb-6"><CheckCircle2 size={28} /></div>
              <h2 className="ek-heading text-2xl md:text-3xl mb-3">Thanks — we'll be in touch within one business day.</h2>
              <p className="text-zinc-600 text-[14.5px]">A founder-led conversation will reach out with a sector-calibrated next step. In the meantime, feel free to browse our <Link to="/insights" className="text-[#7737F6] hover:underline">latest insights</Link> or our <Link to="/resources" className="text-[#7737F6] hover:underline">public resources</Link>.</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
